import React from 'react';
import { Anchor, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import type {
  ActiveContainersStats,
  InTransitStats,
  ArrivingSoonStats,
  DischargedStats,
  TransitCostsStats,
} from '../../interfaces/extendedStatistics.interface';

/* ============================================================
   Active Containers
   ============================================================ */
interface StatCardActiveProps {
  data: ActiveContainersStats;
  isLoading?: boolean;
}

export function StatCardActive({ data, isLoading }: StatCardActiveProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';

  const buckets = [
    {
      key: 'inTransit' as const,
      color: 'var(--st-info)',
      labelRo: 'În tranzit',
      labelEn: 'In transit',
    },
    {
      key: 'discharged' as const,
      color: 'var(--accent)',
      labelRo: 'Descărcate',
      labelEn: 'Discharged',
    },
    {
      key: 'inDelivery' as const,
      color: 'var(--st-success)',
      labelRo: 'În livrare',
      labelEn: 'In delivery',
    },
    {
      key: 'completed' as const,
      color: 'var(--text-muted)',
      labelRo: 'Finalizate',
      labelEn: 'Completed',
    },
  ];

  const counts = data.breakdown;
  const sum =
    counts.inTransit + counts.discharged + counts.inDelivery + counts.completed || 1;

  return (
    <div className="stat-card">
      <div className="stat-top">
        <div className="stat-label">
          <span className="lbl-dot" />
          {isRo ? 'Containere active' : 'Active containers'}
        </div>
      </div>
      <div className="stat-value-row">
        <span className="stat-value">{isLoading ? '—' : data.total}</span>
        <span className="stat-unit">{isRo ? 'containere' : 'containers'}</span>
      </div>
      <div className="stat-visual">
        <div className="seg-bar">
          {buckets.map((b) =>
            counts[b.key] > 0 ? (
              <div
                key={b.key}
                className="seg"
                style={{ width: `${(counts[b.key] / sum) * 100}%`, background: b.color }}
                title={`${isRo ? b.labelRo : b.labelEn}: ${counts[b.key]}`}
              />
            ) : null,
          )}
        </div>
        <div className="seg-legend">
          {buckets
            .filter((b) => counts[b.key] > 0)
            .slice(0, 3)
            .map((b) => (
              <div key={b.key} className="item">
                <span className="sw" style={{ background: b.color }} />
                <span className="lbl">{isRo ? b.labelRo : b.labelEn}</span>
                <span className="v">{counts[b.key]}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   In Transit
   ============================================================ */
interface StatCardInTransitProps {
  data: InTransitStats;
  isLoading?: boolean;
}

export function StatCardInTransit({ data, isLoading }: StatCardInTransitProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const topOrigins = data.byOrigin.slice(0, 4);

  return (
    <div className="stat-card">
      <div className="stat-top">
        <div className="stat-label">
          <span className="lbl-dot" style={{ background: 'var(--st-info)' }} />
          {isRo ? 'În tranzit' : 'In transit'}
        </div>
      </div>
      <div className="stat-value-row">
        <span className="stat-value">{isLoading ? '—' : data.total}</span>
        <span className="stat-unit">
          {data.vesselsCount > 0
            ? isRo
              ? `pe ${data.vesselsCount} ${data.vesselsCount === 1 ? 'navă' : 'nave'}`
              : `on ${data.vesselsCount} ${data.vesselsCount === 1 ? 'vessel' : 'vessels'}`
            : isRo
            ? 'containere'
            : 'containers'}
        </span>
      </div>
      <div className="stat-visual">
        {topOrigins.length > 0 ? (
          <div className="route-chips">
            {topOrigins.map(({ port, count }) => (
              <span key={port} className="route-chip">
                {port}
                <span className="c">{count}</span>
              </span>
            ))}
          </div>
        ) : (
          <div className="muted fs-12">
            {isRo ? 'Niciun container în tranzit' : 'No containers in transit'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Arriving Soon
   ============================================================ */
interface StatCardArrivingProps {
  data: ArrivingSoonStats;
  isLoading?: boolean;
}

export function StatCardArriving({ data, isLoading }: StatCardArrivingProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  // Pe export backendul păstrează numele câmpului (`arrivingSoon`), dar conținutul
  // sunt PLECĂRI din Constanța — deci doar eticheta se inversează.
  const isExport = direction === 'export';
  const byDay = data.byDay.length === 14 ? data.byDay : new Array(14).fill(0);
  const maxDay = Math.max(...byDay, 1);

  return (
    <div className="stat-card">
      <div className="stat-top">
        <div className="stat-label">
          <span className="lbl-dot" style={{ background: 'var(--accent)' }} />
          {isExport
            ? isRo
              ? 'Pleacă curând'
              : 'Departing soon'
            : isRo
            ? 'Sosesc curând'
            : 'Arriving soon'}
        </div>
        <span className="stat-delta flat">14{isRo ? 'z' : 'd'}</span>
      </div>
      <div className="stat-value-row">
        <span className="stat-value">{isLoading ? '—' : data.total}</span>
        <span className="stat-unit">{isRo ? 'containere' : 'containers'}</span>
      </div>
      <div className="stat-visual">
        <div
          className="cal-strip"
          title={
            isExport
              ? isRo
                ? 'Plecări în următoarele 14 zile'
                : 'Departures over next 14 days'
              : isRo
              ? 'Sosiri în următoarele 14 zile'
              : 'Arrivals over next 14 days'
          }
        >
          {byDay.map((cnt, i) => {
            const intensity =
              cnt === 0 ? '' : cnt / maxDay > 0.66 ? ' has lg' : cnt > 0 ? ' has' : '';
            return (
              <div
                key={i}
                className={'cal-day' + intensity + (i === 0 ? ' today' : '')}
                title={cnt > 0 ? `+${i}d · ${cnt}` : `+${i}d`}
              >
                {cnt > 0 ? cnt : ''}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Discharged
   ============================================================ */
interface StatCardDischargedProps {
  data: DischargedStats;
  isLoading?: boolean;
}

export function StatCardDischarged({ data, isLoading }: StatCardDischargedProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const { total, mostRecentDays } = data;

  return (
    <div className="stat-card">
      <div className="stat-top">
        <div className="stat-label">
          <span className="lbl-dot" style={{ background: 'var(--st-success)' }} />
          {isRo ? 'Containere descărcate' : 'Discharged containers'}
        </div>
      </div>
      <div className="stat-value-row">
        <span className="stat-value">{isLoading ? '—' : total}</span>
        <span className="stat-unit">{isRo ? 'containere' : 'containers'}</span>
      </div>
      <div className="stat-visual">
        {total > 0 && mostRecentDays != null ? (
          <div className="discharged-recent">
            <Anchor size={13} strokeWidth={2} />
            <span className="discharged-recent-label">
              {isRo ? 'Cea mai recentă descărcare' : 'Most recent discharge'}
            </span>
            <span className="discharged-recent-val">
              {mostRecentDays === 0
                ? isRo
                  ? 'azi'
                  : 'today'
                : mostRecentDays === 1
                ? isRo
                  ? 'ieri'
                  : 'yesterday'
                : isRo
                ? `acum ${mostRecentDays}z`
                : `${mostRecentDays}d ago`}
            </span>
          </div>
        ) : (
          <div className="stat-empty-ok">
            <Check size={14} strokeWidth={2.5} />
            {isRo ? 'Niciun container în așteptare' : 'No containers awaiting'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Estimated costs (import — in-transit B/Ls, next 6 months)
   ============================================================ */
const MONTH_SHORT_RO = ['ian', 'feb', 'mar', 'apr', 'mai', 'iun', 'iul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG_RO = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
];
const MONTH_LONG_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function sixMonthMeta(lang: 'ro' | 'en'): { month: number; year: number; short: string; long: string }[] {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date());
  const [y, m] = today.split('-').map(Number);
  const shortNames = lang === 'ro' ? MONTH_SHORT_RO : MONTH_SHORT_EN;
  const longNames = lang === 'ro' ? MONTH_LONG_RO : MONTH_LONG_EN;
  const out: { month: number; year: number; short: string; long: string }[] = [];
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(Date.UTC(y, m - 1 + i, 1));
    const month = d.getUTCMonth();
    const year = d.getUTCFullYear();
    out.push({ month, year, short: shortNames[month], long: longNames[month] });
  }
  return out;
}

function compactMoney(n: number, lang: 'ro' | 'en'): string {
  if (n <= 0) return '';
  if (n < 1000) return Math.round(n).toString();
  const k = n / 1000;
  if (k >= 10) return `${Math.round(k)}k`;
  const one = k.toFixed(1);
  return `${lang === 'ro' ? one.replace('.', ',') : one}k`;
}

interface StatCardTransitCostsProps {
  data: TransitCostsStats;
  isLoading?: boolean;
}

export function StatCardTransitCosts({ data, isLoading }: StatCardTransitCostsProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const byMonth = data.byMonth.length === 6 ? data.byMonth : new Array(6).fill(0);
  const maxMonth = Math.max(...byMonth, 1);
  const months = sixMonthMeta(lang);
  const currency = (data.currency || 'USD').toUpperCase();
  const totalLabel = isLoading
    ? '—'
    : new Intl.NumberFormat(isRo ? 'ro-RO' : 'en-US', {
        maximumFractionDigits: 0,
      }).format(data.total || 0);

  return (
    <div className="stat-card">
      <div className="stat-top">
        <div className="stat-label">
          <span className="lbl-dot" style={{ background: 'var(--st-success)' }} />
          {isRo ? 'Costuri estimate' : 'Estimated costs'}
        </div>
        <span className="stat-delta flat">{isRo ? '6 luni' : '6 mo'}</span>
      </div>
      <div className="stat-value-row">
        <span className="stat-value">{totalLabel}</span>
        <span className="stat-unit">{currency}</span>
      </div>
      <div className="stat-visual">
        <div
          className="cal-strip months"
          title={
            isRo
              ? 'Navlu ofertat pe B/L-urile în tranzit, după ETA'
              : 'Quoted ocean freight for in-transit B/Ls, by ETA'
          }
        >
          {months.map((m, i) => {
            const cnt = byMonth[i] || 0;
            const intensity =
              cnt === 0 ? '' : cnt / maxMonth > 0.66 ? ' has lg' : cnt > 0 ? ' has' : '';
            const full = new Intl.NumberFormat(isRo ? 'ro-RO' : 'en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(cnt);
            return (
              <div
                key={`${m.year}-${m.month}`}
                className={'cal-month' + intensity + (i === 0 ? ' today' : '')}
                title={cnt > 0 ? `${m.long} ${m.year} · ${full} ${currency}` : `${m.long} ${m.year}`}
              >
                <span className="m-lbl">{m.short}</span>
                <span className="m-val">{compactMoney(cnt, lang)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
